import { ref, type Ref } from 'vue'
import type { RuntimeFieldValueMap } from '@/features/Inventory/ObjectListEdit/model/runtimeFieldBindings'
import { useHttp } from '@/shared/lib'

export type PseudosqlValueResult = {
  value_result: string | null
  error: string | null
}

type PseudosqlRecomputeResponse = Record<string, PseudosqlValueResult>

/** Числовые поля часто хранят "0.0" — приводим к "0" до POST recompute. */
function normalizePseudosqlFieldValue(raw: unknown): string {
  if (raw == null) return ''
  const s = String(raw).trim()
  if (!s) return ''
  if (/^-?\d+$/.test(s)) return s
  const n = Number(s)
  if (Number.isFinite(n) && Number.isInteger(n)) return String(n)
  if (Number.isFinite(n) && Math.trunc(n) === n) return String(Math.trunc(n))
  return s
}

function normalizeFieldValuesForRecompute(
  values: RuntimeFieldValueMap,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(values ?? {})) {
    out[key] = normalizePseudosqlFieldValue(value)
  }
  return out
}

export function usePassportPseudosqlRecompute(params: {
  elementId: Ref<number>
  typeId: Ref<number>
  fieldValues: Ref<RuntimeFieldValueMap>
}) {
  const http = useHttp()
  const pseudosqlValues = ref<PseudosqlRecomputeResponse>({})
  const isLoading = ref(false)
  /** Номер поколения запроса: ответ применяем, только если он ещё актуален. */
  let recomputeGeneration = 0

  const recompute = async () => {
    const eid = Number(params.elementId.value)
    const tid = Number(params.typeId.value)
    const body = { field_values: normalizeFieldValuesForRecompute(params.fieldValues.value) }

    let url: string
    if (Number.isFinite(eid) && eid > 0) {
      url = `/objects/${eid}/passport/recompute`
    } else if (Number.isFinite(tid) && tid > 0) {
      url = `/objects/passport/recompute?type_id=${tid}`
    } else {
      return
    }

    const gen = ++recomputeGeneration
    isLoading.value = true
    try {
      const res = await http.post(url, body)
      if (gen !== recomputeGeneration) return
      pseudosqlValues.value = res.data ?? {}
    } finally {
      if (gen === recomputeGeneration) isLoading.value = false
    }
  }

  /**
   * Сбрасывает кеш value_result и гасит летящий запрос (при смене версии/объекта
   * устаревший ответ recompute не должен перекрывать срез из GET passport).
   */
  const reset = () => {
    recomputeGeneration++
    pseudosqlValues.value = {}
    isLoading.value = false
  }

  return {
    pseudosqlValues,
    recompute,
    reset,
    isLoading,
  }
}
